import React, { useRef, useState, useEffect } from "react";
import { Volume2, VolumeX, ArrowRight } from "lucide-react";
import { LOGO_URL, waUrl } from "../lib/constants";
import { trackViewContent } from "../lib/pixel";

const GOLD = "#c9a227";

/**
 * /session — Private HNWI link page.
 * Single page: small brand header → vertical video (autoplay muted loop) →
 * one-line creds → "Reserve a private session" WhatsApp CTA.
 * Not in nav. Not linked anywhere. Shared via direct link only.
 */
const Session = () => {
    const videoRef = useRef(null);
    const [muted, setMuted] = useState(true);

    useEffect(() => {
        trackViewContent({
            content_name: "Session Page — HNWI Link",
            content_category: "private_share",
        });
    }, []);

    const toggleSound = () => {
        const v = videoRef.current;
        if (!v) return;
        const next = !muted;
        v.muted = next;
        setMuted(next);
        if (!next) v.play().catch(() => {});
    };

    return (
        <main
            data-testid="session-page"
            style={{
                minHeight: "100vh",
                backgroundColor: "#0a0a0a",
                color: "#fff",
                fontFamily: "Inter, sans-serif",
                padding: "40px 20px 60px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            {/* HEADER */}
            <header
                style={{
                    width: "100%",
                    maxWidth: 520,
                    textAlign: "center",
                    marginBottom: 28,
                }}
            >
                <img
                    src={LOGO_URL}
                    alt="The Reset Sanctuary"
                    style={{
                        width: 56,
                        height: 56,
                        objectFit: "contain",
                        margin: "0 auto 16px",
                        display: "block",
                    }}
                />
                <p
                    style={{
                        color: GOLD,
                        fontSize: 10.5,
                        letterSpacing: "0.42em",
                        textTransform: "uppercase",
                        margin: "0 0 12px",
                        fontWeight: 600,
                    }}
                >
                    The Reset Sanctuary
                </p>
                <h1
                    className="font-serif"
                    style={{
                        color: "#fff",
                        fontSize: "clamp(26px, 5vw, 36px)",
                        fontWeight: 400,
                        lineHeight: 1.15,
                        letterSpacing: "-0.02em",
                        margin: "0 0 10px",
                    }}
                >
                    A private session,{" "}
                    <span style={{ color: GOLD }}>filmed in the UAE.</span>
                </h1>
                <p
                    style={{
                        color: "#aaa",
                        fontSize: 14,
                        lineHeight: 1.65,
                        margin: 0,
                        fontStyle: "italic",
                        fontWeight: 300,
                    }}
                >
                    Frequencies engaged. Brain shifting. Client
                    deep in rest within minutes.
                </p>
            </header>

            {/* VERTICAL VIDEO */}
            <section
                style={{
                    width: "100%",
                    maxWidth: 360,
                    position: "relative",
                    border: `1px solid rgba(201,162,39,0.3)`,
                    backgroundColor: "#000",
                    overflow: "hidden",
                    aspectRatio: "9 / 16",
                    boxShadow:
                        "0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(201,162,39,0.06)",
                    marginBottom: 28,
                }}
                data-testid="session-video-wrap"
            >
                <video
                    ref={videoRef}
                    data-testid="session-video"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    poster="/brochure-assets/essam-poster.jpg"
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        backgroundColor: "#000",
                    }}
                >
                    <source
                        src="/brochure-assets/essam.mp4"
                        type="video/mp4"
                    />
                </video>
                <button
                    type="button"
                    onClick={toggleSound}
                    data-testid="session-sound-toggle"
                    aria-label={muted ? "Tap for sound" : "Mute"}
                    style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "8px 12px",
                        backgroundColor: "rgba(10,10,10,0.7)",
                        backdropFilter: "blur(8px)",
                        WebkitBackdropFilter: "blur(8px)",
                        border: `1px solid ${GOLD}`,
                        color: GOLD,
                        fontSize: 10,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        fontWeight: 600,
                        fontFamily: "Inter, sans-serif",
                        cursor: "pointer",
                    }}
                >
                    {muted ? (
                        <>
                            <VolumeX size={12} /> Tap for Sound
                        </>
                    ) : (
                        <>
                            <Volume2 size={12} /> Sound On
                        </>
                    )}
                </button>
            </section>

            {/* CREDS LINE */}
            <p
                style={{
                    color: GOLD,
                    fontSize: 10.5,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    margin: "0 0 8px",
                    fontWeight: 600,
                    textAlign: "center",
                    maxWidth: 560,
                }}
                data-testid="session-creds-eyebrow"
            >
                Tatva K · Recovery by Frequency
            </p>
            <p
                style={{
                    color: "#bbb",
                    fontSize: 13,
                    lineHeight: 1.7,
                    margin: "0 0 6px",
                    textAlign: "center",
                    maxWidth: 560,
                    fontWeight: 300,
                }}
                data-testid="session-creds"
            >
                TEDx Speaker · 150M+ aggregate streams · Berklee Music
                Therapy Masterclass · ISTA &amp; IPHM Certified · Featured
                on Insight Timer.
            </p>
            <p
                style={{
                    color: GOLD,
                    fontSize: 10,
                    letterSpacing: "0.34em",
                    textTransform: "uppercase",
                    margin: "0 0 32px",
                    fontWeight: 600,
                    textAlign: "center",
                }}
            >
                Hand-produced. Human. No AI.
            </p>

            {/* CTA */}
            <a
                href={waUrl(
                    "Hello, I'd like to reserve a private session with Tatva K. (From the private session video link.)",
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="session-cta"
                className="btn-gold-solid"
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "16px 32px",
                    fontSize: 13,
                    letterSpacing: "0.3em",
                }}
            >
                Reserve a Private Session
                <ArrowRight size={14} />
            </a>

            {/* Secondary book CTA */}
            <a
                href={waUrl(
                    "Hi, I'd like to book my session now. (From the private session video link.)",
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="session-book-now"
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    color: GOLD,
                    textDecoration: "none",
                    fontSize: 12,
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    fontFamily: "Inter, sans-serif",
                    borderBottom: `1px solid rgba(201,162,39,0.4)`,
                    paddingBottom: 4,
                    marginTop: 18,
                }}
            >
                Book Your Session Now
                <ArrowRight size={12} />
            </a>

            <p
                style={{
                    color: "#666",
                    fontSize: 11,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    margin: "20px 0 0",
                    fontWeight: 400,
                }}
            >
                Via WhatsApp · UAE-wide · In-villa or studio
            </p>
        </main>
    );
};

export default Session;
